import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import Keycloak from 'keycloak-js';
import { ErrorMessageComponent } from '../../shared/modals/error-message/error-message.component';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  userInfo: any;
  availableLangs = ['es', 'en'];
  private readonly LANGUAGE_KEY = 'preferredLanguage';
  private readonly DEFAULT_LANGUAGE = 'es';

  constructor(
    private translate: TranslateService,
    private keycloak: Keycloak,
    private dialog: MatDialog,
  ) {
    this.initializeLanguage();
  }

  getAvailableLangs() {
    return this.availableLangs;
  }

  /**
   * Inicializa el idioma desde localStorage o usa el idioma por defecto
   */
  private initializeLanguage(): void {
    const savedLanguage = this.getStoredLanguage();
    const languageToUse = savedLanguage ?? this.DEFAULT_LANGUAGE;

    this.translate.setDefaultLang(this.DEFAULT_LANGUAGE);
    this.translate.use(languageToUse);
  }

  /**
   * Cambia el idioma y lo guarda en localStorage
   */
  changeLanguage(lang: string): void {
    if (this.availableLangs.includes(lang)) {
      this.translate.use(lang);
      localStorage.setItem(this.LANGUAGE_KEY, lang);
    }
  }

  /**
   * Obtiene el idioma actual
   */
  getCurrentLanguage(): string {
    return (
      this.translate.currentLang ||
      this.getStoredLanguage() ||
      this.DEFAULT_LANGUAGE
    );
  }

  /**
   * Obtiene el idioma guardado en localStorage
   */
  private getStoredLanguage(): string | null {
    return localStorage.getItem(this.LANGUAGE_KEY);
  }

  /**
   * Obtiene el idioma por defecto
   */
  getDefaultLanguage(): string {
    return this.DEFAULT_LANGUAGE;
  }

  setUserInfo(userInfo: any) {
    this.userInfo = userInfo;
  }

  getUserInfo() {
    return this.userInfo;
  }

  /**
   * Obtiene la primera letra del nombre de usuario en mayúscula
   */
  getFirstLetterOfName(): string {
    if (this.userInfo?.name) {
      return this.userInfo.name.charAt(0).toUpperCase();
    }
    return '';
  }

  /**
   * Calcula el tiempo transcurrido desde una fecha dada hasta ahora
   * @param dateString Fecha en formato ISO string (ej: "2025-06-02T13:14:50.151Z")
   * @returns Tiempo transcurrido en formato humano (ej: "1 h.", "15 min.", "2 días")
   */
  getTimeAgo(dateString: string): string {
    const now = new Date();
    const pastDate = new Date(dateString);
    const diffInMs = now.getTime() - pastDate.getTime();

    // Convertir a diferentes unidades
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInMonths = Math.floor(diffInDays / 31);
    const diffInYears = Math.floor(diffInDays / 365);

    // Si han pasado más de 12 meses (1 año o más)
    if (diffInYears >= 1) {
      return diffInYears === 1
        ? this.translate.instant('time.one_year')
        : this.translate.instant('time.years', { count: diffInYears });
    }

    // Si han pasado más de 31 días (1 mes o más)
    if (diffInMonths >= 1) {
      return diffInMonths === 1
        ? this.translate.instant('time.one_month')
        : this.translate.instant('time.months', { count: diffInMonths });
    }

    // Si han pasado más de 24 horas (1 día o más)
    if (diffInDays >= 1) {
      return diffInDays === 1
        ? this.translate.instant('time.one_day')
        : this.translate.instant('time.days', { count: diffInDays });
    }

    // Si ha pasado más de 1 hora
    if (diffInHours >= 1) {
      return this.translate.instant('time.hours', { count: diffInHours });
    }

    // Si ha pasado menos de 1 hora
    if (diffInMinutes >= 1) {
      return this.translate.instant('time.minutes', { count: diffInMinutes });
    }

    // Si ha pasado menos de 1 minuto
    return this.translate.instant('time.now');
  }

  showErrorMessage(error: any, title: string, route: string) {
    if (error.status === 403 && error.error?.code === 'NETWORK_FORBIDDEN') {
      return;
    }

    let errorMessage = '';
    // Intentar parsear error.error si es una cadena JSON
    if (error?.error && typeof error.error === 'string') {
      try {
        const parsedError = JSON.parse(error.error);
        if (Array.isArray(parsedError.message)) {
          // Si message es un array, tomar el primer mensaje
          errorMessage =
            this.translate.instant(parsedError.message[0]) ||
            parsedError.message[0] ||
            errorMessage;
        } else {
          errorMessage =
            this.translate.instant(parsedError.message) ||
            parsedError.message ||
            errorMessage;
        }
      } catch (e) {
        // Si no se puede parsear, usar el valor directo
        errorMessage = this.translate.instant(error.error) || error.error;
      }
    } else if (error?.error?.message) {
      // Si error.error ya es un objeto con message
      if (Array.isArray(error.error.message)) {
        // Si message es un array, tomar el primer mensaje
        errorMessage =
          this.translate.instant(error.error.message[0]) ||
          error.error.message[0];
      } else {
        errorMessage =
          this.translate.instant(error.error.message) || error.error.message;
      }
    } else if (error?.message) {
      // Si error tiene message directamente
      if (Array.isArray(error.message)) {
        // Si message es un array, tomar el primer mensaje
        errorMessage =
          this.translate.instant(error.message[0]) || error.message[0];
      } else {
        errorMessage = this.translate.instant(error.message) || error.message;
      }
    }

    const dialogRef = this.dialog.open(ErrorMessageComponent, {
      panelClass: 'dialog',
      data: {
        errorTitle:
          this.translate.instant(title) ||
          this.translate.instant('error.title'),
        errorMessage: errorMessage || this.translate.instant(error),
        route: route || '',
      },
    });
  }

  async redirectToKeycloakLogin() {
    await this.keycloak.login();
  }
}
